import { AlertTriangle, Lock, SearchX, WifiOff } from 'lucide-react';
import { RetryIcon } from '@/shared/config/entity-icons';
import { Button } from '../Button';
import { cn } from '@/shared/lib/cn';
import styles from './ErrorState.module.css';

export interface ErrorStateProps {
  message?: string | null;
  title?: string;
  onRetry?: () => void;
  className?: string;
}

type Kind = 'forbidden' | 'notFound' | 'network' | 'generic';

function classify(message?: string | null): Kind {
  const m = (message ?? '').toLowerCase();
  if (/(доступ|forbidden|403|запрещ|прав)/.test(m)) return 'forbidden';
  if (/(не найден|not found|404|удален|удалён)/.test(m)) return 'notFound';
  if (/(network|сет|соедин|failed to fetch|timeout|таймаут|offline)/.test(m)) return 'network';
  return 'generic';
}

const PRESET: Record<Kind, { Icon: typeof AlertTriangle; title: string; muted: boolean }> = {
  forbidden: { Icon: Lock, title: 'Доступ запрещён', muted: true },
  notFound: { Icon: SearchX, title: 'Ничего не найдено', muted: true },
  network: { Icon: WifiOff, title: 'Нет соединения', muted: false },
  generic: { Icon: AlertTriangle, title: 'Что-то пошло не так', muted: false },
};

/**
 * Единое оформление ошибок загрузки: иконка + заголовок + пояснение + повтор.
 * Тип (иконка/заголовок) выводится из текста ошибки, если title не задан явно.
 *
 * @use не удалось загрузить данные; парный к EmptyState, вид подбирает сам по тексту ошибки.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({ message, title, onRetry, className }) => {
  const kind = classify(message);
  const { Icon, title: presetTitle, muted } = PRESET[kind];
  const heading = title ?? presetTitle;
  const hint =
    message && message.trim().toLowerCase() !== heading.toLowerCase()
      ? message
      : 'Попробуйте обновить страницу или повторить позже';

  return (
    <div className={cn(styles.root, className)} role="alert">
      <span className={cn(styles.icon, muted && styles.iconMuted)} aria-hidden>
        <Icon size={24} />
      </span>
      <div className={styles.title}>{heading}</div>
      <p className={styles.message}>{hint}</p>
      {onRetry && (
        <Button variant="ghost" size="sm" type="button" onClick={onRetry} className={styles.retry}>
          <RetryIcon size={15} aria-hidden />
          Повторить
        </Button>
      )}
    </div>
  );
};
